import { apiClient } from '../client';
import { API_ENDPOINTS } from '../config';

export interface DashboardStats {
  conversations: number;
  contacts: number;
  messages: number;
  campaigns: number;
  responseRate: number;
  abandonedCheckouts: number;
  aiCredits?: {
    total: number;
    used: number;
    available: number;
    plan?: string | null;
  } | null;
  aiInsight?: {
    analysisDate: string | null;
    status: string | null;
    summary: string | null;
    aiInsights: string[];
    metrics: {
      total_conversations?: number | null;
      analyzed_conversations?: number | null;
      success_conversations?: number | null;
      failure_conversations?: number | null;
      neutral_conversations?: number | null;
      success_rate_percent?: number | null;
      failure_rate_percent?: number | null;
      neutral_rate_percent?: number | null;
      token_usage?: {
        input_tokens?: number;
        output_tokens?: number;
        total_tokens?: number;
        total_cost_usd?: number;
        cost_breakdown?: {
          input_cost_usd?: number;
          output_cost_usd?: number;
        };
      } | null;
      [key: string]: any;
    } | null;
    error: string | null;
    triggeredAt: string | null;
    updatedAt: string | null;
  } | null;
}

export interface RecentActivity {
  id: string;
  type: 'message' | 'campaign' | 'contact';
  title: string;
  description: string;
  timestamp: string;
  status?: string;
  contact?: {
    name: string;
    phone_number: string;
  };
}

export interface AgentDashboardStats {
  stats: {
    contactsAssigned: number;
    totalConversations: number;
    messagesSent: number;
  };
  weeklyActivity: {
    day: string;
    messages: number;
    conversations: number;
  }[];
  monthlyContactsGrowth: {
    month: string;
    contacts: number;
  }[];
}

export const dashboardApi = {
  /**
   * Get dashboard statistics
   */
  async getStats(
    businessAccountId: string | null,
    organizationId?: string | null
  ): Promise<{ data: DashboardStats; error: string | null }> {
    try {
      if (!businessAccountId && !organizationId) {
        return {
          data: {
            conversations: 0,
            contacts: 0,
            messages: 0,
            campaigns: 0,
            responseRate: 0,
            abandonedCheckouts: 0,
            aiCredits: null,
            aiInsight: null,
          },
          error: 'No business account or organization selected',
        };
      }

      // Prefer optimized backend endpoint for accurate totals
      const optimized = await apiClient.get<DashboardStats>(API_ENDPOINTS.DASHBOARD_STATS, {
        params: {
          ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
          ...(organizationId ? { organization_id: organizationId } : {}),
        },
      });

      if (optimized && !optimized.error && optimized.data) {
        return { data: optimized.data as any, error: null };
      }

      // Fallback: compute from paginated endpoints if optimized route unavailable
      const [conversationsRes, contactsRes, messagesRes, campaignsRes] = await Promise.all([
        apiClient.get(API_ENDPOINTS.CONVERSATIONS, { params: { business_account_id: businessAccountId, limit: 1 } }),
        apiClient.get(API_ENDPOINTS.CONTACTS, { params: { business_account_id: businessAccountId, limit: 1 } }),
        apiClient.get(API_ENDPOINTS.MESSAGES, { params: { business_account_id: businessAccountId, limit: 1 } }),
        apiClient.get(API_ENDPOINTS.CAMPAIGNS, { params: { business_account_id: businessAccountId, limit: 1 } }),
      ]);

      const conversationsCount = conversationsRes.totalCount ?? conversationsRes.count ?? 0;
      const contactsCount =
        (typeof contactsRes.totalCount === 'number' ? contactsRes.totalCount : undefined) ??
        (typeof contactsRes.filteredTotalCount === 'number' ? contactsRes.filteredTotalCount : undefined) ??
        (typeof contactsRes.count === 'number' ? contactsRes.count : 0);
      const messagesCount = messagesRes.totalCount ?? messagesRes.count ?? 0;
      const campaignsCount = campaignsRes.totalCount ?? campaignsRes.count ?? 0;

      // Fallback path runs only when the optimized /dashboard/stats endpoint
      // failed. We deliberately do NOT fabricate a response rate here — the
      // legacy code returned `Math.min(94 + Math.random()*4, 99)` which made
      // the Dashboard appear to show a 94-98% delivery health figure that had
      // zero basis in reality. Returning 0 lets the UI render "—" / hide the
      // metric instead of lying.
      return {
        data: {
          conversations: conversationsCount,
          contacts: contactsCount,
          messages: messagesCount,
          campaigns: campaignsCount,
          responseRate: 0,
          abandonedCheckouts: 0,
        },
        error: null,
      };
    } catch (error) {
      return {
        data: {
          conversations: 0,
          contacts: 0,
          messages: 0,
          campaigns: 0,
          responseRate: 0,
          abandonedCheckouts: 0,
          aiCredits: null,
          aiInsight: null,
        },
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Get recent activity
   */
  async getRecentActivity(businessAccountId: string | null): Promise<{ data: RecentActivity[]; error: string | null }> {
    try {
      if (!businessAccountId) {
        return {
          data: [],
          error: 'No business account selected',
        };
      }

      // Get recent conversations and campaigns in parallel with business account filtering
      const [conversationsRes, campaignsRes] = await Promise.all([
        apiClient.get(API_ENDPOINTS.CONVERSATIONS, {
          params: {
            businessAccountId: businessAccountId,
            orderBy: 'updated_at',
            ascending: false,
            limit: 5,
          },
        }),
        apiClient.get(API_ENDPOINTS.CAMPAIGNS, {
          params: {
            businessAccountId: businessAccountId,
            orderBy: 'updated_at',
            ascending: false,
            limit: 3,
          },
        }),
      ]);

      const conversations = conversationsRes.data || [];
      const campaigns = campaignsRes.data || [];

      // Debug: Log the actual API responses (remove in production)
      // void(0);
      // void(0);

      const activity: RecentActivity[] = [];

      // Add conversation activities
      for (const conv of conversations) {
        // Use contact data that's already included in the conversation response
        const contact = conv.contact;
        // void(0);
        // void(0);

        // Use contact name if available, otherwise use phone number as fallback
        const contactName = contact?.name || conv.phoneNumber || 'Unknown';

        const conversationTimestamp =
          conv.lastMessageAt ||
          conv.last_message_at ||
          conv.lastMessage?.createdAt ||
          conv.last_message?.created_at ||
          conv.updatedAt ||
          conv.updated_at ||
          conv.createdAt ||
          conv.created_at ||
          new Date().toISOString();

        // Determine description based on last message
        let description = `Conversation with ${contactName}`;
        let title = 'Conversation Activity';

        if (conv.last_message) {
          if (conv.last_message.is_inbound) {
            title = 'New Message';
            description = `Message from ${contactName}`;
          } else {
            title = 'Message Sent';
            description = `Message to ${contactName}`;
          }
        } else {
          title = 'New Conversation';
          description = `Started with ${contactName}`;
        }

        activity.push({
          id: `conv-${conv.id}`,
          type: 'message',
          title: title,
          description: description,
          timestamp: conversationTimestamp,
          status: conv.status,
          contact: {
            name: contactName,
            phone_number: contact?.phoneNumber || conv.phoneNumber || 'N/A',
          },
        });
      }

      // Add campaign activities
      campaigns.forEach((campaign) => {
        // void(0);
        // void(0);
        // void(0);
        // void(0);

        // Use the most recent timestamp available
        const timestamp = campaign.updated_at || campaign.completed_at || campaign.created_at;
        // void(0);

        // If no timestamp is available, use current time as fallback
        const finalTimestamp = timestamp || new Date().toISOString();
        // void(0);

        activity.push({
          id: `campaign-${campaign.id}`,
          type: 'campaign',
          title: 'Campaign updated',
          description: `${campaign.name} - ${campaign.status}`,
          timestamp: finalTimestamp,
          status: campaign.status,
        });
      });

      // Sort by timestamp
      activity.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return {
        data: activity,
        error: null,
      };
    } catch (error) {
      return {
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};

export const agentDashboardApi = {
  /**
   * Get agent dashboard statistics
   */
  async getAgentDashboardStats(businessAccountId: string | null): Promise<{ data: AgentDashboardStats | null; error: string | null }> {
    try {
      const response = await apiClient.get<AgentDashboardStats>(API_ENDPOINTS.AGENT_DASHBOARD, {
        params: { businessAccountId },
      });

      if (response && response.data) {
        return { data: response.data, error: null };
      }

      return { data: null, error: 'No data returned' };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};
